import { PROVIDER_PAYOUT_STATUS } from '@/types/payment-payout';

/** Textos da tela Recebimentos (plano, seção 6.4). */

export const RECEIVABLES_EMPTY = {
  title: 'Nada por aqui ainda',
  body: 'Seu primeiro pagamento automático aparece aqui.',
};

export const RECEIVABLES_TOTALS = {
  incoming: {
    title: 'A caminho da sua conta',
    helper: 'Repasses já enviados, com data de chegada.',
  },
  releasing: {
    title: 'Liberando',
    helper: 'Pago pelo cliente, ainda no prazo.',
  },
  receivedThisMonth: {
    title: 'Recebido no mês',
    helper: 'Líquido que já caiu na sua conta.',
  },
};

export const RECEIVABLES_SCHEDULE = {
  title: 'Quando libera',
  hint: 'Toque num dia para ver os pagamentos. Depois de liberado, o repasse para o seu banco sai sozinho.',
  emptyState: 'Nada liberando nos próximos dias.',
  seeAll: 'Ver tudo que está liberando',
};

/** Tela "Liberando": cada pagamento que ainda não liberou, com a data. */
export const RECEIVABLES_RELEASING = {
  title: 'Liberando',
  totalLabel: 'Total liberando',
  countLabel: 'Pagamentos',
  listTitle: 'Pagamentos e quando cada um libera',
  hint: 'Na data de liberação o valor fica disponível e o repasse para o seu banco sai sozinho, sem você precisar sacar.',
  releasesOn: 'Libera em',
  paidOn: 'pago em',
  emptyTitle: 'Nada liberando agora',
  emptyBody: 'Quando um cliente pagar, o valor aparece aqui com a data em que libera.',
  clientFallback: 'Cliente',
};

export const RECEIVABLES_PAYOUTS = {
  title: 'Repasses',
  emptyState: 'Nenhum repasse ainda.',
  detailTitle: 'Cobranças deste repasse',
};

export const RECEIVABLES_SCREEN = {
  title: 'Recebimentos',
  subtitle: 'Quanto entrou e quando chega',
};

export const RECEIVABLES_PERIOD = {
  title: 'Período',
  sheetTitle: 'Ver recebimentos de',
  heroTitle: 'Líquido no período',
  grossLabel: 'Bruto',
  feeLabel: 'Taxas',
  refundsLabel: 'Reembolsos',
  disputedLabel: 'Contestado',
  netLabel: 'Líquido',
  payoutsLabel: 'Repasses no período',
  options: {
    '7d': 'Últimos 7 dias',
    '30d': 'Últimos 30 dias',
    '90d': 'Últimos 90 dias',
    all: 'Último ano',
    custom: 'Escolher datas',
  },
  customHint: 'Até 366 dias',
};

/** Card "Evolução": o que o período rendeu, dia a dia. */
export const RECEIVABLES_EVOLUTION = {
  title: 'Evolução',
  empty: 'Nenhum pagamento automático neste período.',
  metrics: {
    net: 'Líquido',
    gross: 'Bruto',
    fee: 'Taxas',
  },
};

/** `GET /payment-account/payouts/:id` (T3.1, T6.2). */
export const RECEIVABLES_PAYOUT_DETAIL = {
  title: 'Repasse',
  accountLabel: 'Conta',
  chargesTitle: 'Cobranças deste repasse',
  chargesEmpty: 'Nenhuma cobrança encontrada para este repasse.',
  chargesTotalLabel: 'Soma das cobranças',
  payoutAmountLabel: 'Valor do repasse',
  /** Diferença entre `chargesNetTotal` e `amount` — arredondamento ou ajuste do Stripe. Nunca escondida (T6.2). */
  adjustmentsLabel: 'Ajustes',
  adjustmentsHint: 'Diferença de arredondamento ou de um ajuste feito pelo provedor de pagamento.',
  clientFallback: 'Cliente',
  failureTitle: 'O que aconteceu',
};

/** Rótulo e tom do `StatusBadge` (`type: 'custom'`) por `PROVIDER_PAYOUT_STATUS` (T3.1, T6.1/T6.2). */
export const PAYOUT_STATUS_LABELS: Record<PROVIDER_PAYOUT_STATUS, string> = {
  [PROVIDER_PAYOUT_STATUS.PENDING]: 'Pendente',
  [PROVIDER_PAYOUT_STATUS.IN_TRANSIT]: 'A caminho',
  [PROVIDER_PAYOUT_STATUS.PAID]: 'Pago',
  [PROVIDER_PAYOUT_STATUS.FAILED]: 'Falhou',
  [PROVIDER_PAYOUT_STATUS.CANCELED]: 'Cancelado',
};

export const PAYOUT_STATUS_VARIANTS: Record<
  PROVIDER_PAYOUT_STATUS,
  'success' | 'warning' | 'error' | 'info' | 'neutral'
> = {
  [PROVIDER_PAYOUT_STATUS.PENDING]: 'warning',
  [PROVIDER_PAYOUT_STATUS.IN_TRANSIT]: 'info',
  [PROVIDER_PAYOUT_STATUS.PAID]: 'success',
  [PROVIDER_PAYOUT_STATUS.FAILED]: 'error',
  [PROVIDER_PAYOUT_STATUS.CANCELED]: 'neutral',
};

/** "Nubank ••1234" · "••1234" sem o nome do banco · "Conta cadastrada" sem nada (T3.1: `bankLast4` chega cru). */
export function formatPayoutAccount(
  bankName: string | null | undefined,
  bankLast4: string | null | undefined,
): string {
  if (!bankLast4) return 'Conta cadastrada';
  return bankName ? `${bankName} ••${bankLast4}` : `••${bankLast4}`;
}

export const RECEIVABLES_NOTICES = {
  payoutFailedTitle: 'Um repasse falhou',
  payoutFailedBody:
    'O provedor de pagamento vai tentar de novo automaticamente. Se persistir, confira os dados da sua conta bancária.',
  disputeOpenTitle: 'Contestação aberta',
  disputeOpenBody:
    'Um cliente contestou um pagamento com o banco dele. Veja o prazo e o que responder.',
};
